



































































































































































import { Component, Prop, Vue, Watch } from "@/shared/vuepack";
import { usePartnerCardIssueCriteriaStore } from "@/admin/store/partnerCardIssueCriteria";
import { CardIssueCustomizationOptionType, CardIssueCustomizationOptionUpdateRequest } from "@/admin/types";
import { CardProvider, FetchState } from "@/shared/types";
import GenericLoadingSpinner from "@/shared/components/ui/GenericLoadingSpinner.vue";
import { useThemeStore } from "@/shared/store/theme";
import _ from "lodash";
import { v4 as uuid } from "uuid";
import { ValidationObserver, ValidationProvider } from "vee-validate";

interface TableItem {
  issueCardCustomizationOptionId: number;
  name: string;
  value: string;
  isDefaultOption: boolean;
  key: string;
  isEditing: boolean;
}

interface SimpleOption {
  name: string;
  value: string;
}

const emptyOption: SimpleOption = {
  name: "",
  value: ""
};

@Component({
  components: {
    GenericLoadingSpinner,
    ValidationObserver,
    ValidationProvider
  }
})
export default class ManageCardCustomizationOptions extends Vue {
  cardIssueCriteriaStore = usePartnerCardIssueCriteriaStore();
  themeStore = useThemeStore();
  isSaving = false;
  options: TableItem[] = [];
  deletedOptions: number[] = [];
  newOption = _.cloneDeep(emptyOption);
  originalOptions = new Map<string, SimpleOption>();
  showSaveConfirmation = false;

  @Prop({ required: true })
  optionType: CardIssueCustomizationOptionType;

  @Prop({ required: true })
  show: { value: boolean };

  get title () {
    return `Manage ${this.getOptionTypeDisplayName()}`;
  }

  get isLoading () {
    return this.cardIssueCriteriaStore.cardIssueCustomizationOptionsFetchState === FetchState.Loading;
  }

  get isLoaded () {
    return this.cardIssueCriteriaStore.cardIssueCustomizationOptionsFetchState === FetchState.Loaded;
  }

  get saveDisabled () {
    return !this.isValid || this.isEditingInProgress || this.isSaving;
  }

  get isValid () {
    return !this.$validator.errors.any();
  }

  get optionTableFields () {
    return [
      { key: "name", label: "Name" },
      { key: "value", label: "Value" },
      { key: "isDefaultOption", label: "Default" },
      { key: "actions", label: "" }
    ];
  }

  get isEditingInProgress () {
    return this.options.some(option => option.isEditing);
  }

  @Watch("cardIssueCriteriaStore.cardIssueCustomizationOptions")
  onCardIssueCustomizationOptionsChanged () {
    this.cloneStoreToModel();
  }

  @Watch("show.value")
  onShowChanged () {
    if (this.show.value) {
      this.cloneStoreToModel();
    } else {
      this.reset();
    }
  }

  mounted () {
    this.cardIssueCriteriaStore.loadCardIssueCustomizationOptions();
  }

  isRowInvalid (key: string) {
    return this.$validator.errors.any(key);
  }

  cloneStoreToModel () {
    const filteredOptions = this.cardIssueCriteriaStore.cardIssueCustomizationOptions
      .filter(o => o.optionType === this.optionType && !o.isDeleted);
    const options = _.cloneDeep(filteredOptions);

    this.options = options.map(option => ({
      issueCardCustomizationOptionId: option.issueCardCustomizationOptionId,
      name: option.name,
      value: option.value,
      isDefaultOption: option.isDefaultOption,
      key: uuid(),
      isEditing: false
    }));
  }

  save () {
    this.$validator.validate().then(result => {
      if (!result) {
        return;
      }

      this.isSaving = true;

      const request: CardIssueCustomizationOptionUpdateRequest = {
        options: this.options.map(option => ({
          issueCardCustomizationOptionId: option.issueCardCustomizationOptionId,
          optionType: this.optionType,
          name: option.name,
          value: option.value,
          isDefaultOption: option.isDefaultOption
        })),
        deletedOptions: this.deletedOptions
      };

      this.cardIssueCriteriaStore.updateCardIssueCustomizationOptions(request)
        .then(() => this.close())
        .catch(() => {/*noop*/})
        .finally(() => {
          this.isSaving = false;
        });
    });
  }

  close () {
    this.show.value = false;
  }

  setDefault (key: string) {
    const updatedOptions = this.options.map(option => {
      if (option.key === key) {
        return { ...option, isDefaultOption: true };
      }

      return { ...option, isDefaultOption: false };
    });

    this.options = updatedOptions;
  }

  addOption () {
    const validator = this.$refs.newOptionValidator as any;

    validator.validate().then(result => {
      if (!result) {
        return;
      }

      if (this.options.some(option => option.value === this.newOption.value)) {
        const valueValidator = this.$refs.newOptionValueValidator as any;

        valueValidator.applyResult({
          errors: ["Value already in use"],
          valid: false,
          failedRules: {}
        });

        return;
      }

      const newOption: TableItem = {
        issueCardCustomizationOptionId: 0,
        name: this.newOption.name,
        value: this.newOption.value,
        isDefaultOption: false,
        key: uuid(),
        isEditing: false
      };

      this.options.push(newOption);
      this.newOption = _.cloneDeep(emptyOption);

      requestAnimationFrame(() => {
        validator.reset();
      });
    });
  }

  deleteOption (item: TableItem) {
    this.options = this.options.filter(option => option.key !== item.key);

    if (item.issueCardCustomizationOptionId > 0) {
      this.deletedOptions.push(item.issueCardCustomizationOptionId);
    }
  }

  beginEdit (item: TableItem) {
    const updatedOptions = this.options.map(option => {
      if (option.key === item.key) {
        return { ...option, isEditing: true };
      }

      return option;
    });

    this.originalOptions.set(item.key, { name: item.name, value: item.value });
    this.options = updatedOptions;
  }

  finishEdit (item: TableItem) {
    if (this.options.some(option => option.value === item.value && option.key !== item.key)) {
      this.$validator.errors.add({
        field: `${item.key}.Value`,
        msg: "Value already in use"
      });

      return;
    }

    const updatedOptions = this.options.map(option => {
      if (option.key === item.key) {
        return { ...option, isEditing: false };
      }

      return option;
    });

    this.originalOptions.delete(item.key);
    this.options = updatedOptions;
  }

  cancelEdit (item: TableItem) {
    const originalOption = this.originalOptions.get(item.key);
    const updatedOptions = this.options.map(option => {
      if (option.key === item.key) {
        return {
          ...option,
          name: originalOption ? originalOption.name : option.name,
          value: originalOption ? originalOption.value : option.value,
          isEditing: false
        };
      }

      return option;
    });

    this.originalOptions.delete(item.key);
    this.options = updatedOptions;
  }

  getOptionTypeDisplayName () {
    switch (this.optionType) {
      case CardIssueCustomizationOptionType.Logo:
        return "Logos";
      default:
        return "Options";
    }
  }

  reset () {
    this.isSaving = false;
    this.options = [];
    this.deletedOptions = [];
    this.newOption = _.cloneDeep(emptyOption);
    this.originalOptions.clear();
    this.showSaveConfirmation = false;
  }
}
